import { type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "@sprout/buttons";
import type { JobPost, Question, SearchSpecificFields } from "../../types/jben/job_post";
import type { BoardConfiguration } from "@/types/jben/board_configuration.ts";
import type { JobDescriptionSummary } from "@/utils/job_description_summary.ts";
import ApplicationFormHeader from "./application_form_header";
import { ApplicationDescription } from "./application_description";
import PanelSummary from "@components/job_search/panel_summary.tsx";
import ApplicationDreamJobBanner from "@components/job_post/application_dream_job_banner.tsx";
import ApplicationQuickApplyBanner from "@components/job_post/application_quick_apply_banner.tsx";
import { ApplyHeading, QuestionFields } from "./question_fields";
import { useApplicationForm } from "./use_application_form";
import "./application_form.scss";

export type { FormField } from "./use_application_form";

interface Props {
  jobPost: JobPost & SearchSpecificFields;
  questions: Question[];
  submitPath: string;
  boardConfiguration: BoardConfiguration;
  onSuccess?: (confirmationPath: string) => void;
  showBasicFields?: boolean;
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
  isRecommendation?: boolean;
  summary?: JobDescriptionSummary | null;
  onCreateAlert?: () => void;
  dirtyRef: RefObject<() => boolean>;
}

export const ApplicationForm = ({
  jobPost,
  questions,
  submitPath,
  boardConfiguration,
  onSuccess,
  showBasicFields = true,
  educationApiUrl,
  dreamJobAvailable = false,
  isRecommendation = false,
  summary,
  onCreateAlert,
  dirtyRef,
}: Props) => {
  const { t } = useTranslation("job_post");

  const form = useApplicationForm({
    jobPost,
    questions,
    submitPath,
    onSuccess,
    showBasicFields,
    educationApiUrl,
    dreamJobAvailable,
    isRecommendation,
    dirtyRef,
  });

  return (
    <>
      <ApplicationFormHeader jobPost={jobPost} onCreateAlert={onCreateAlert} />
      <PanelSummary summary={summary} />
      <ApplicationDescription
        introduction={jobPost.introduction}
        content={jobPost.content}
        conclusion={jobPost.conclusion}
        payRanges={jobPost.pay_ranges}
      />
      <hr className="application-description--divider" />
      <div className="jben-form jben-form--container">
        <ApplyHeading />
        <form id={"application-form"} onSubmit={form.handleSubmit} className="application--form">
          <QuestionFields
            form={form}
            jobPost={jobPost}
            boardConfiguration={boardConfiguration}
            educationApiUrl={educationApiUrl}
          />

          {dreamJobAvailable && (
            <ApplicationDreamJobBanner
              marked={form.markAsDreamJob}
              onChange={form.setMarkAsDreamJob}
            />
          )}

          {form.showQuickApplyBanner && (
            <ApplicationQuickApplyBanner
              checked={form.saveToQuickApply}
              onChange={form.setSaveToQuickApply}
            />
          )}

          {form.submitErrorMessage && (
            <p id="submit-error" className="helper-text helper-text--error" aria-live="polite">
              {form.submitErrorMessage}
            </p>
          )}

          <div className="application--submit">
            <PrimaryButton
              type="submit"
              onClick={form.handleSubmit}
              loading={form.submitting}
              disabled={form.submitting}
            >
              {t("application.submit")}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </>
  );
};
